import {
  GeneralPermissions,
  SettingsPermissions,
} from "@redotech/account-common/permissions";
import { t } from "@redotech/redo-i18n";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { ReturnsSettingsParentBreadcrumb } from "../../../../setting/returns/returns-settings-parent-breadcrumb";
import { defaultChildRoute } from "../legacy-redirects";

export const generalSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      get breadcrumb() {
        return t("Billing", { context: "merchant settings label for billing" });
      },
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.VIEW_BILLING,
        SettingsPermissions.SET_BILLING_SETTINGS,
      ],
    },
    path: "billing",
    children: [
      {
        index: true,
        lazy: async () => {
          const { BillingPage } =
            await import("../../../../setting/billing/billing");
          return { Component: BillingPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Inventory management", {
              context:
                "merchant settings breadcrumb for inventory management billing",
            });
          },
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "ims",
        lazy: async () => {
          const { ImsBilling } =
            await import("../../../../setting/billing/products/ims/ims-billing");
          return { Component: ImsBilling };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Outbound");
          },
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "outbound",
        children: [
          {
            lazy: async () => {
              const { OutboundBillingDetail } =
                await import("../../../../setting/billing/products/outbound-billing-detail");
              return { Component: OutboundBillingDetail };
            },
            children: [
              defaultChildRoute("general"),
              {
                path: "general",
                lazy: async () => {
                  const { BalanceTransactionTable } =
                    await import("../../../../setting/billing/outbound-labels-ledger/balance-transaction-table");
                  return { Component: BalanceTransactionTable };
                },
              },
              {
                path: "balance",
                lazy: async () => {
                  const { OutboundLabelBalancePage } =
                    await import("../../../../setting/outbound-label-balance");
                  return { Component: OutboundLabelBalancePage };
                },
              },
              {
                path: "disputes",
                lazy: async () => {
                  const { SurchargeDisputeTable } =
                    await import("../../../../setting/billing/surcharge-disputes/surcharge-dispute-table");
                  return { Component: SurchargeDisputeTable };
                },
              },
            ],
          },
          {
            async lazy() {
              const { InvoiceDetailPageWrapper, breadcrumb } =
                await import("../../../../setting/billing/outbound-labels-ledger/invoice-detail-page-wrapper");
              return {
                Component: InvoiceDetailPageWrapper,
                handle: {
                  breadcrumb,
                  permission: SettingsPermissions.SET_GENERAL_SETTINGS,
                },
              };
            },
            path: "outbound-invoice/:invoiceId",
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Returns");
          },
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "returns",
        children: [
          {
            lazy: async () => {
              const { ReturnsBillingDetail } =
                await import("../../../../setting/billing/products/returns/returns-billing-detail");
              return { Component: ReturnsBillingDetail };
            },
            children: [
              defaultChildRoute("general"),
              {
                path: "general",
                lazy: async () => {
                  const { ReturnsBilling } =
                    await import("../../../../setting/billing/products/returns/returns-billing");
                  return { Component: ReturnsBilling };
                },
              },
            ],
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Supplies");
          },
          permission: SettingsPermissions.SET_BILLING_SETTINGS,
        },
        path: "supplies",
        lazy: async () => {
          const { RedoSuppliesBillingPage } =
            await import("../../../../setting/billing/payment-methods/supplies-billing-page");
          return { Component: RedoSuppliesBillingPage };
        },
      },
      {
        path: ":productSlug",
        lazy: async () => {
          const { BillingProductDetail, productBreadcrumb } =
            await import("../../../../setting/billing/billing-product-detail");
          return {
            Component: BillingProductDetail,
            handle: {
              breadcrumb: productBreadcrumb,
              permission: SettingsPermissions.SET_GENERAL_SETTINGS,
            },
          };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Brand kit", {
          context: "merchant settings label for brand assets and styling",
        });
      },
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "brand-kit",
    lazy: async () => {
      const { BrandKitPage } =
        await import("@redotech/redo-merchant-general-app/setting/brand-kit/brand-kit");
      return { Component: BrandKitPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Communication", {
          context: "merchant settings label for customer communication",
        });
      },
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "communication",
    lazy: async () => {
      const { CommunicationPage } =
        await import("@redotech/redo-merchant-general-app/setting/communication/communication");
      return { Component: CommunicationPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Customer portal", {
          context: "merchant settings label for the customer returns portal",
        });
      },
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      ],
    },
    path: "customer-portal",
    element: <ReturnsSettingsParentBreadcrumb legacyPath="customer-portal" />,
    children: [
      {
        index: true,
        lazy: async () => {
          const { CustomerPortalPage: CustomerPortal } =
            await import("@redotech/redo-merchant-general-app/setting/customer-portal/customer-portal-page");
          return { Component: CustomerPortal };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Bundles");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "bundles",
        lazy: async () => {
          const { BundlesPage } =
            await import("../../../../setting/bundles/bundles");
          return { Component: BundlesPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Exchange Agent Guidance");
          },
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "guidance",
        lazy: async () => {
          const { ExchangeAgentGuidancePage } =
            await import("../../../../setting/exchange-agent-guidance/exchange-agent-guidance-page");
          return { Component: ExchangeAgentGuidancePage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Flows");
          },
          breadcrumbNavigate: false,
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "flows",
        children: [
          {
            handle: {
              get breadcrumb() {
                return t("AB test analytics");
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "ab-test-analytics",
            lazy: async () => {
              const { ReturnFlowABTestAnalytics } =
                await import("../../../../setting/return-flow/ab-test-analytics");
              return { element: <ReturnFlowABTestAnalytics /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Claims");
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "claims",
            lazy: async () => {
              const { ClaimsFlowEditor } =
                await import("../../../../setting/flows/claims-flow-editor");
              return { element: <ClaimsFlowEditor /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Claim finalization", {
                  context:
                    "merchant settings label for the editor configuring how claim flows finish",
                });
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "claims-finalize",
            lazy: async () => {
              const { ClaimsFinalizeFlowEditor } =
                await import("../../../../setting/flows/claims-finalize-flow-editor");
              return { element: <ClaimsFinalizeFlowEditor /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Returns");
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "returns",
            lazy: async () => {
              const { ReturnsFlowEditor } =
                await import("../../../../setting/flows/returns-flow-editor");
              return { element: <ReturnsFlowEditor /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Return finalization", {
                  context:
                    "merchant settings label for the editor configuring how return flows finish",
                });
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "returns-finalize",
            lazy: async () => {
              const { ReturnsFinalizeFlowEditor } =
                await import("../../../../setting/flows/returns-finalize-flow-editor");
              return { element: <ReturnsFinalizeFlowEditor /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Warranties");
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "warranties",
            lazy: async () => {
              const { WarrantiesFlowEditor } =
                await import("../../../../setting/flows/warranties-flow-editor");
              return { element: <WarrantiesFlowEditor /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Warranty finalization", {
                  context:
                    "merchant settings label for the editor configuring how warranty flows finish",
                });
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "warranties-finalize",
            lazy: async () => {
              const { WarrantiesFinalizeFlowEditor } =
                await import("../../../../setting/flows/warranties-finalize-flow-editor");
              return { element: <WarrantiesFinalizeFlowEditor /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Warranty registration", {
                  context:
                    "merchant settings label for the editor configuring warranty registration flows",
                });
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "warranties-registration",
            lazy: async () => {
              const { WarrantiesRegistrationFlowEditor } =
                await import("../../../../setting/flows/warranties-registration-flow-editor");
              return { element: <WarrantiesRegistrationFlowEditor /> };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Withdrawals", {
                  context:
                    "returns settings breadcrumb for customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
                });
              },
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "withdrawals",
            lazy: async () => {
              const { WithdrawalsFlowEditor } =
                await import("../../../../setting/flows/withdrawals-flow-editor");
              return { element: <WithdrawalsFlowEditor /> };
            },
          },
        ],
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Customer widget", {
          context: "merchant settings label for the customer widget",
        });
      },
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_AI_SETTINGS,
        SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
        SettingsPermissions.SET_SUPPORT_SETTINGS,
      ],
    },
    path: "customer-widget",
    lazy: async () => {
      const { CustomerWidgetSettingsPage } =
        await import("../../../../setting/customer-widget/customer-widget-settings");
      return { Component: CustomerWidgetSettingsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Developer", {
          context: "merchant settings label for developer tools",
        });
      },
      permission: GeneralPermissions.MANAGE_TEAM_DEVELOPMENT,
    },
    path: "developer",
    children: [
      {
        path: "",
        lazy: async () => {
          const { DeveloperPage } =
            await import("@redotech/redo-merchant-general-app/setting/developer");
          return { Component: DeveloperPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Webhook Details");
          },
        },
        path: "webhooks/:webhookId",
        lazy: async () => {
          const { WebhookDetailPage } =
            await import("@redotech/redo-merchant-general-app/setting/developer/webhook-detail-page");
          return { Component: WebhookDetailPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Groups", {
          context: "merchant settings label for team groups",
        });
      },
      permission: GeneralPermissions.MANAGE_TEAM_USERS,
    },
    path: "user-groups",
    lazy: async () => {
      const { UserGroupsPage } =
        await import("../../../../setting/user-groups/user-groups-page");
      return { Component: UserGroupsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Notifications", {
          context: "merchant settings label for team notifications",
        });
      },
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "notifications",
    lazy: async () => {
      const { NotificationsPage } =
        await import("@redotech/redo-merchant-general-app/setting/notification/notifications");
      return { Component: NotificationsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Portal builder", {
          context: "merchant settings label for the customer portal builder",
        });
      },
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      ],
    },
    path: "portal-builder",
    lazy: async () => {
      const { CustomerPortalBuilderSettingsPage } =
        await import("@redotech/redo-merchant-general-app/setting/customer-portal-builder/customer-portal-builder-settings");
      return { Component: CustomerPortalBuilderSettingsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Roles", {
          context: "merchant settings label for team roles",
        });
      },
      permission: GeneralPermissions.MANAGE_TEAM_ROLES,
    },
    path: "roles",
    lazy: async () => {
      const { RolesSettingsPage } =
        await import("@redotech/redo-merchant-general-app/setting/roles/roles-settings");
      return { Component: RolesSettingsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Users", {
          context: "merchant settings label for team users",
        });
      },
      permission: GeneralPermissions.MANAGE_TEAM_USERS,
    },
    path: "users",
    children: [
      {
        path: "",
        lazy: async () => {
          const { UsersPage } =
            await import("@redotech/redo-merchant-general-app/setting/user/user");
          return { Component: UsersPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("User information");
          },
        },
        path: ":userId",
        lazy: async () => {
          const { ProfilePage } =
            await import("@redotech/redo-merchant-general-app/setting/user/profile-page");
          return { Component: ProfilePage };
        },
      },
    ],
  },
];
